// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import styled from '@emotion/styled';
const sprintf = require('sprintf-js').sprintf;

import {
  spacing,
  fontWeight,
  fontSize,
  mediaMax,
  borderRadius,
  isMediaMax,
} from 'presentations/views/styles';
import {
  primary,
  gray01,
  black,
  white,
  gray05,
  gray04,
} from 'presentations/views/styles/color';
import Icon from 'presentations/views/components/atoms/Icon';
import Button from 'presentations/views/components/atoms/Button';
import { DateTime, formatToYMD, getDateTime } from 'infras/datetime';
import { FrequencyType } from 'domains/models/event_type_calendar/frequencyType';
import Image from 'presentations/views/components/atoms/Image';
import EventTypeCalendar from 'domains/models/event_type_calendar';
import { SyntheticEvent } from 'react';

export interface Props {
  timeZone: string;
  eventTypeCalendars: EventTypeCalendar[];
  onClickAddCalendar: (e: SyntheticEvent<Element, Event>) => void;
  onClickCalendarItem: (e: SyntheticEvent, calendar: EventTypeCalendar) => void;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);

  return (
    <ListBody>
      <ListBodyHeader>
        <ListTitle>{t('Set time')}</ListTitle>
        <AddCalendarButton
          variant="text"
          color="primary"
          onClick={props.onClickAddCalendar}
        >
          {t('Add calendar')}
        </AddCalendarButton>
      </ListBodyHeader>
      <CalendarList>
        {props.eventTypeCalendars.map((calendar) => {
          const startedAt = getDateTime(calendar.startedAt, props.timeZone);
          let endedAt: DateTime | null = null;
          if (calendar.endedAt) {
            endedAt = getDateTime(calendar.endedAt, props.timeZone);
          }
          let frequencyText: string | null = null;
          switch (calendar.frequencyType) {
            case FrequencyType.Weekly:
              frequencyText =
                calendar.frequencyInterval === 1
                  ? t('Every Week')
                  : t('Week', { number: calendar.frequencyInterval });
              frequencyText += t(`week day ${calendar.wday}`);
              break;
            case FrequencyType.Daily:
            case FrequencyType.Monthly:
            case FrequencyType.Yearly:
              // TODO: 値が来るようになったら対応
              break;
            default:
              break;
          }

          return (
            <CalendarListItem
              key={calendar.id}
              onClick={(e) => props.onClickCalendarItem(e, calendar)}
            >
              <Icon name="chevron-right" size="20px" color="primary" />
              <CalendarListItemInner>
                <CalendarListItemDateTime className="calendarListItemHover">
                  {frequencyText ? (
                    <>
                      <FrequencyIcon
                        name="repeat"
                        size="20px"
                        color="inherit"
                      />
                      {frequencyText}
                      {` ${calendar.startHour}:${sprintf(
                        '%02d',
                        calendar.startMinute,
                      )} ~ ${calendar.endHour}:${sprintf(
                        '%02d',
                        calendar.endMinute,
                      )}`}
                    </>
                  ) : (
                    <>
                      {formatToYMD(startedAt, undefined, { delimiter: '/' })}
                      {` ${calendar.startHour}:${sprintf(
                        '%02d',
                        calendar.startMinute,
                      )} ~ ${calendar.endHour}:${sprintf(
                        '%02d',
                        calendar.endMinute,
                      )}`}
                    </>
                  )}
                </CalendarListItemDateTime>
                <EndDate>
                  {frequencyText && endedAt && (
                    <>
                      <EndDateImage
                        src="pages/dashboard/timer-stop-outline.svg"
                        alt={t('End date')}
                        width={20}
                        height={20}
                      />
                      {formatToYMD(
                        endedAt.hour === 0
                          ? endedAt.minus({ day: 1 })
                          : endedAt,
                        undefined,
                        { delimiter: '/' },
                      )}
                    </>
                  )}
                </EndDate>
              </CalendarListItemInner>
            </CalendarListItem>
          );
        })}
      </CalendarList>
    </ListBody>
  );
}

const ListBody = styled.div`
  padding: ${spacing(24)};
  background-color: ${white};
  border-radius: ${borderRadius(16)};
  border: 1px solid ${gray04};

  ${mediaMax.tablet} {
    padding: ${spacing(12)};
  }
`;
const ListBodyHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 36px;
  margin-bottom: ${spacing(16)};
`;
const ListTitle = styled.div`
  font-weight: ${fontWeight('bold')};
`;
const AddCalendarButton = styled(Button)`
  font-size: ${fontSize(12)};
`;
const CalendarList = styled.div``;

const CalendarListItem = styled.div`
  cursor: pointer;
  display: flex;
  height: 46px;
  padding: ${spacing(12)} 0;
  border-bottom: 1px solid ${gray05};
  fill: ${gray01};

  &:first-of-type {
    border-top: 1px solid ${gray05};
  }

  &:hover .calendarListItemHover {
    color: ${primary};
    fill: ${primary};
  }

  ${mediaMax.tablet} {
    align-items: center;
    height: 72px;
  }
`;
const CalendarListItemInner = styled.div`
  display: flex;
  justify-content: space-between;
  flex: 1;

  ${mediaMax.tablet} {
    flex-direction: column;
  }
`;
const CalendarListItemDateTime = styled.div`
  display: flex;
`;
const FrequencyIcon = styled(Icon)`
  margin: 0 ${spacing(8)};
  flex-shrink: 0;

  ${mediaMax.tablet} {
    margin: 0 ${spacing(4)} 0 0;
  }
`;
const EndDate = styled.div`
  display: flex;
  color: ${black};

  &:hover {
    color: ${black};
  }
`;
const EndDateImage = styled(Image)`
  margin-right: ${spacing(8)};
  flex-shrink: 0;

  ${mediaMax.tablet} {
    margin: 0 ${spacing(4)};
  }
`;
